import { OperatorE } from '@/middleware/queries/Operator';
import {
    AdvancedFilterGroupNode,
    AdvancedFilterNode,
    ADVANCED_FILTER_MAX_DEPTH,
} from './AdvancedFilterTypes';

export interface AdvancedFilterValidationResult {
    valid: boolean;
    errorsByNodeId: Record<string, string[]>;
    maxDepthSeen: number;
}

export function validateAdvancedFilter(
    root: AdvancedFilterGroupNode | null,
    maxDepth: number = ADVANCED_FILTER_MAX_DEPTH
): AdvancedFilterValidationResult {
    if (!root) {
        return { valid: true, errorsByNodeId: {}, maxDepthSeen: 0 };
    }

    const errorsByNodeId: Record<string, string[]> = {};
    let maxDepthSeen = 0;

    function addError(nodeId: string, message: string) {
        if (!errorsByNodeId[nodeId]) {
            errorsByNodeId[nodeId] = [];
        }
        errorsByNodeId[nodeId].push(message);
    }

    function validateNode(node: AdvancedFilterNode, depth: number, isRoot: boolean) {
        maxDepthSeen = Math.max(maxDepthSeen, depth);

        if (depth > maxDepth) {
            addError(node.id, `Maksimal nøsting er overskredet`);
            return;
        }

        switch (node.kind) {
            case 'group':
                if (!isRoot && node.children.length === 0) {
                    addError(node.id, 'Gruppen er tom');
                }
                node.children.forEach(child => validateNode(child, depth + 1, false));
                break;
            case 'egenskap':
                if (!node.egenskapstype) {
                    addError(node.id, 'Egenskapstype og operator mangler');
                    break;
                }
                if (node.operator === null) {
                    addError(node.id, 'Operator mangler');
                    break;
                }
                if (
                    node.operator !== null &&
                    node.operator !== OperatorE.HAS_VALUE &&
                    node.operator !== OperatorE.NO_VALUE &&
                    node.verdi === null
                ) {
                    addError(node.id, 'Verdi mangler');
                }
                break;
            case 'overlapp':
            case 'relasjon':
                if (node.targetTypeId === null) {
                    addError(node.id, 'Vegobjekttype mangler');
                }
                if (node.child) {
                    validateNode(node.child, depth + 1, false);
                }
                break;
        }
    }

    validateNode(root, 1, true);

    return {
        valid: Object.keys(errorsByNodeId).length === 0,
        errorsByNodeId,
        maxDepthSeen,
    };
}

export function canAddAtDepth(
    currentDepth: number,
    maxDepth: number = ADVANCED_FILTER_MAX_DEPTH
): boolean {
    return currentDepth < maxDepth;
}

export function canAddGroupAtDepth(
    currentDepth: number,
    maxDepth: number = ADVANCED_FILTER_MAX_DEPTH
): boolean {
    return canAddAtDepth(currentDepth + 1, maxDepth);
}
